import * as React from 'react';
import { NavLink } from 'react-router-dom';

import { MenuItem } from '@common/react/objects/MenuItem';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import { getLocalStorageValue, setLocalStorageValue } from '@common/react/utils/localStorage/localStorage';

import '@common/react/scss/components/menu.scss';

import { Icon } from '@app/components/Routes/DashboardMenu';
import FavoritesButton from '@app/components/UI/Buttons/FavoritesButton/FavoritesButton';
import { isTextInclude } from '@app/components/Utils';

interface MenuItemWithIcon extends MenuItem {
	icon?: Array<string | Icon>;
	isFavorite?: boolean;
}

interface MenuItemProps extends Omit<MenuProps, 'items' | 'className'> {
	item: MenuItemWithIcon;
	forceOpen?: boolean;
	search?: string;
	className?: string;
	liRef?: any;
	additional?: React.ReactNode;
	onMouseMove?: () => void;
	onClick?: (e) => void;
}

interface MenuProps {
	className?: string;
	items: Array<MenuItemWithIcon>;
	withChildren?: boolean;
	basePath?: string;
	pathKey?: string;
	defaultOpen?: boolean;
	forceOpen?: boolean;
	withSearch?: boolean;
	withFavoriteButtons?: boolean;
}

const IconNode: React.FC<{icon: Icon | string}> = ({ icon }) => {
	if (typeof icon === 'string') {
		return <i className={`fa ${icon}`} />;
	}
	if (icon.type === 'image') {
		return <img title={icon.caption} alt={icon.caption} src={icon.value} style={{ display: 'inline-block', width: 14 }} />;
	}
	if (icon.type === 'icon' || !icon.type) {
		return <i title={icon.caption} className={`fa ${icon.value}`} />;
	}
	return <span title={icon.caption}>{icon.value}</span>;
};

export const CustomItem: React.FC<MenuItemProps> = (props) => {
	const {
		defaultOpen,
		basePath,
		item,
		search,
		pathKey,
		withChildren,
		forceOpen,
		className: propsClassName,
		liRef,
		additional,
		onMouseMove,
		withFavoriteButtons = true,
		onClick,
	} = props;
	const [open, setOpen] = React.useState(forceOpen && defaultOpen);
	const [searchOpen, setSearchOpen] = React.useState(true);

	React.useEffect(() => {
		setOpen(JSON.parse(getLocalStorageValue(`${basePath}${item.name}menu-item`, 'false')));
	}, []);

	const toggleMenu = () => {
		if (forceOpen) {
			return;
		}
		if (!search) {
			setOpen((prev) => {
				setLocalStorageValue(`${basePath}${item.name}menu-item`, !prev);
				return !prev;
			});
		} else {
			setSearchOpen((prev) => !prev);
		}
	};

	React.useEffect(() => {
		if (search) {
			setSearchOpen(!!isTextInclude(search, item.name));
		}
	}, [search]);

	const list = React.useMemo(() => {
		return item.children && item.children?.list
			.map((el) => (search && isTextInclude(search, el.name) ? { ...el, className: `${el.className || ''} hover` } : el))
			.filter((el) => searchOpen || isTextInclude(search || '', el.name));
	}, [item.children, searchOpen, search]);

	const isOpen = forceOpen || !!search || (item.isOpen ?? open);

	const condition = withChildren && item.children && item.children.list && item.children.list.length > 0;

	const path = item[pathKey || 'path'];

	const { exact = false } = item;

	const className = `${propsClassName || ''} menu-component__item ${open && condition
		? 'menu-component__item_open' : ''} ${
		condition
			? 'menu-component__item_with-children' : ''} ${item.className || ''}`;

	const icon = item.icon?.map((icon, i) => ({ icon, i })).map(({ icon, i }) => (
		<React.Fragment key={i}>
			{i > 0 && i < (item.icon?.length || 0) && ' \\ '}
			<IconNode icon={icon} />
		</React.Fragment>
	));

	return (
		<li ref={liRef} className={className} onMouseMove={onMouseMove}>
			{path ? item.externalLink
				? (
					<a className="menu-component__item-link" href={item.path} target="_blank" rel="noreferrer" onClick={onClick}>
						{item.name}
						{icon}
					</a>
				)
				: (
					<div className="menu-component__item-link-wrapper">
						<NavLink
							end={exact}
							to={basePath + path}
							className={({ isActive }) => (isActive
								? 'menu-component__item-link menu-component__item-link_active'
								: 'menu-component__item-link')
							}
							onClick={onClick}
						>
							{item.name}
							{icon}
							{additional}
						</NavLink>
						{withFavoriteButtons
							? <FavoritesButton url={item.path} title={item.name} style={{ opacity: item.isFavorite ? 1 : undefined }} />
							: null}
					</div>
				)
				: <span className="menu-component__item-link" onClick={toggleMenu} style={{ cursor: 'pointer' }}>{item.name}</span>}
			{condition
			&& (
				<>
					<i
						className={`icons menu-component__item-toggle ${(search ? searchOpen : isOpen)
							? 'fa fa-chevron-up' : 'fa fa-chevron-down'}`}
						onClick={toggleMenu}
					/>
					<div className={`menu-component__item-children-container ${isOpen ? 'isOpen' : ''}`}>
						<ul className="menu-component__children">
							{list?.map((child) => (
								<CustomItem
									item={child}
									key={`${child.name}${child.path}`}
									withChildren={withChildren}
									basePath={child.basePath || basePath || ''}
									pathKey={pathKey}
									search={search}
									withFavoriteButtons={withFavoriteButtons}
								/>
							))}
						</ul>
					</div>
				</>
			)}
		</li>
	);
};

export const Menu: React.FC<MenuProps> = ({
	className = '',
	items,
	withChildren = false,
	basePath,
	pathKey,
	defaultOpen,
	forceOpen,
	withSearch = true,
	withFavoriteButtons,
}) => {
	const [search, setSearch] = React.useState<string>('');
	const [key, setKey] = React.useState(0);
	const handleChange = (e) => setSearch(e.currentTarget.value);

	const menuItems = React.useMemo(() => {
		return (items || []).reduce((acc: Array<MenuItemWithIcon>, value: MenuItemWithIcon) => {
			let current = value;
			const includeText = isTextInclude(search, value.name);

			if (value.children && !includeText) {
				current = JSON.parse(JSON.stringify(value));
				current.children!.list = current.children!.list.filter((el) => isTextInclude(search, el.name));
			}

			return includeText || current.children?.list.length ? [...acc, value] : [...acc];
		}, []);
	}, [items, search]);

	/* React.useEffect(() => {
		if (search) {
			setSearch('');
			setKey(Math.random());
		}
	}, [location.pathname]); */

	return (
		<>
			{withSearch && <SimpleSearchInput
				name="menu-search"
				key={key}
				onChange={handleChange}
				defaultValue={search}
				withoutForm
			/>}
			<ul className={`menu-component ${className}`}>
				{menuItems.map((item) => (
					<CustomItem
						search={search}
						defaultOpen={defaultOpen}
						key={`${item.name}${item.path}`}
						item={item}
						forceOpen={forceOpen}
						withChildren={item.children ? true : withChildren}
						basePath={item.basePath || basePath || ''}
						pathKey={pathKey}
						withFavoriteButtons={withFavoriteButtons}
					/>
				))}
			</ul>
		</>
	);
};
